import React, { useMemo, ReactNode, useContext } from "react";
import { makeEventIcons } from "./EventIcons";
import { CountdownTimerContext } from "../../../../components";
import type { MatchTimelineEvent } from "../../../../components/matchTimeline";

const inferTotalMinutes = (events: MatchTimelineEvent[], status?: any) => {
  const maxMin = Math.max(
    0,
    ...events
      .map((e) => (Number(e.minute) || 0) + (Number(e.extraMinutes) || 0))
      .filter((x) => Number.isFinite(x))
  );

  const nsc = String(status?.nsc ?? "").trim();
  const hasET =
    ["1018", "1019", "1020", "1021"].includes(nsc) ||
    events.some((e) => e.type === "extra_time");

  if (hasET || maxMin >= 91) return 120; // classic ET case
  if (maxMin <= 20) return 12; // short e-sports style
  return 90; // default football
};

const SLOT_COUNT = 90;
const MID_SLOTS = 45;

const ratioToTrackPercent = (r: number) => {
  const clamp = (x: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, x));
  const ratio = clamp(r, 0, 1);
  if (ratio <= 0.5) {
    return (ratio / 0.5) * 48; // 0..48
  }
  return 48 + 4 + ((ratio - 0.5) / 0.5) * 48; // 52..100
};

const minuteToSlot = (minute: number, totalMinutes: number) => {
  const m = Math.max(1, Math.min(totalMinutes, minute));
  return Math.min(
    SLOT_COUNT - 1,
    Math.floor(((m - 1) / totalMinutes) * SLOT_COUNT)
  );
};

export interface TimelineProgressHeatmapProps {
  events: MatchTimelineEvent[];
  homeColor: string;
  awayColor: string;
  status?: any;
  currentMinute?: number;
}

interface MinuteData {
  slotIndex: number;
  homeEvents: number;
  awayEvents: number;
  totalEvents: number;
  isPassed: boolean;
}

const ICON_TYPES = new Set(["red_card", "yellow_card", "goal", "substitution"]);
const SMALL_ICONS = makeEventIcons({
  style: { width: "3vw", height: "3vw" },
});

const hasHeatmapData = (list: MatchTimelineEvent[] | undefined | null) => {
  if (!Array.isArray(list) || list.length === 0) return false;
  // show only if there is at least one timed event or a major marker
  return list.some((e) => {
    const m = Number(e?.minute) || 0;
    return (
      m > 0 ||
      e?.type === "first_half_end" ||
      e?.type === "full_time" ||
      e?.type === "extra_time"
    );
  });
};

export const TimelineProgressHeatmap: React.FC<
  TimelineProgressHeatmapProps
> = ({ events, homeColor, awayColor, currentMinute = 0, status }) => {
  const dataReady = useMemo(() => hasHeatmapData(events), [events]);
  const totalMinutes = useMemo(
    () => inferTotalMinutes(events, status),
    [events, status]
  );

  const timer = useContext(CountdownTimerContext) as {
    minutes: string;
    seconds: string;
  } | null;

  const liveMinute = useMemo(() => {
    const fallback = Number(currentMinute) || 1;
    if (timer?.minutes != null && timer?.seconds != null) {
      const fractional = Number(timer.minutes) + Number(timer.seconds) / 60;
      return Math.max(1, Math.min(totalMinutes, fractional));
    }
    return Math.max(1, Math.min(totalMinutes, fallback));
  }, [timer?.minutes, timer?.seconds, currentMinute, totalMinutes]);

  const liveRatio = liveMinute / totalMinutes;
  const passedSlots = Math.floor(liveRatio * SLOT_COUNT);

  const heatmapData = useMemo(() => {
    const slotData: MinuteData[] = Array.from(
      { length: SLOT_COUNT },
      (_, i) => ({
        slotIndex: i,
        homeEvents: 0,
        awayEvents: 0,
        totalEvents: 0,
        isPassed: i < passedSlots,
      })
    );

    events.forEach((event: MatchTimelineEvent) => {
      const m = Number(event.minute) || 0;
      if (m > 0 && m <= totalMinutes) {
        const slotIdx = minuteToSlot(m, totalMinutes);
        if (event.team === "home") slotData[slotIdx].homeEvents++;
        else if (event.team === "away") slotData[slotIdx].awayEvents++;
        slotData[slotIdx].totalEvents++;
      }
    });

    return slotData;
  }, [events, passedSlots, totalMinutes]);

  const [minuteIconsHome, minuteIconsAway] = useMemo(() => {
    const mapHome = new Map<number, Array<ReactNode>>();
    const mapAway = new Map<number, Array<ReactNode>>();
    events.forEach((ev) => {
      if (!ICON_TYPES.has(ev.type)) return;
      const m = Number(ev.minute) || 0;
      if (m < 1 || m > totalMinutes) return;

      const iconNode =
        ev.type === "goal"
          ? SMALL_ICONS["goal"]
          : ev.type === "yellow_card"
          ? SMALL_ICONS["yellow_card"]
          : ev.type === "red_card"
          ? SMALL_ICONS["red_card"]
          : ev.type === "substitution"
          ? SMALL_ICONS["substitution"]
          : null;
      if (!iconNode) return;

      const slotIdx = minuteToSlot(m, totalMinutes);
      if (ev.team === "home") {
        const arr = mapHome.get(slotIdx) || [];
        arr.push(iconNode);
        mapHome.set(slotIdx, arr);
      } else if (ev.team === "away") {
        const arr = mapAway.get(slotIdx) || [];
        arr.push(iconNode);
        mapAway.set(slotIdx, arr);
      }
    });
    return [mapHome, mapAway];
  }, [events, totalMinutes]);

  const maxEvents = Math.max(...heatmapData.map((d) => d.totalEvents), 1);

  const renderBar = (data: MinuteData) => {
    const topPct = Math.min(
      50,
      (data.homeEvents / Math.max(1, maxEvents)) * 50
    );
    const bottomPct = Math.min(
      50,
      (data.awayEvents / Math.max(1, maxEvents)) * 50
    );

    return (
      <div
        key={data.slotIndex}
        className="relative h-full bg-[#5f5f5f40]"
        style={{ opacity: data.isPassed ? 1 : 0.45 }}
      >
        <div
          className="absolute left-0 right-0"
          style={{
            bottom: "50%",
            height: `${topPct}%`,
            background: homeColor,
            transition: "height 200ms linear",
          }}
        />
        <div
          className="absolute left-0 right-0"
          style={{
            top: "50%",
            height: `${bottomPct}%`,
            background: awayColor,
            transition: "height 200ms linear",
          }}
        />
      </div>
    );
  };

  const leftPercent = ratioToTrackPercent(liveRatio);

  if (!dataReady || totalMinutes < 45) {
    return null;
  }

  return (
    <div className="relative w-full pt-[5vw] pb-[6vw]">
      <div className="relative">
        <div
          className="pointer-events-none absolute left-0 right-0"
          style={{ bottom: "calc(8vw + 2vw)", height: "4vw", zIndex: 6 }}
        >
          {Array.from(minuteIconsHome.entries()).map(([slotIndex, icons]) =>
            icons.map((IconNode, i) => (
              <div
                key={`${slotIndex}-home-${i}`}
                className="absolute flex -translate-x-1/2 items-center justify-center"
                style={{
                  left: `${ratioToTrackPercent((slotIndex + 1) / SLOT_COUNT)}%`,
                  bottom: 0,
                  marginBottom: `${i * 1}vw`,
                }}
              >
                {IconNode}
              </div>
            ))
          )}
        </div>

        <div
          className="pointer-events-none absolute left-0 right-0"
          style={{ top: "calc(8vw + 2vw)", height: "4vw", zIndex: 5 }}
        >
          {Array.from(minuteIconsAway.entries()).map(([slotIndex, icons]) =>
            icons.map((IconNode, i) => (
              <div
                key={`${slotIndex}-away-${i}`}
                className="absolute flex -translate-x-1/2 items-center justify-center"
                style={{
                  left: `${ratioToTrackPercent((slotIndex + 1) / SLOT_COUNT)}%`,
                  marginTop: `${i * 1.8}vw`,
                }}
              >
                {IconNode}
              </div>
            ))
          )}
        </div>

        <div
          className="absolute top-0 -translate-x-1/2 bg-green"
          style={{
            left: `${leftPercent}%`,
            width: "0.5vw",
            height: "8vw",
            borderRadius: "0.2vw",
            zIndex: 4,
          }}
        />

        <div className="relative flex h-[8vw] items-center">
          <div className="absolute inset-x-0 top-1/2 -z-10 h-[0.3vw] -translate-y-1/2 bg-simulatorBg" />
          <div
            className="grid h-full w-[48%]"
            style={{
              gridTemplateColumns: "repeat(45, minmax(0, 1fr))",
              columnGap: "0.25vw",
            }}
          >
            {heatmapData.slice(0, MID_SLOTS).map((d) => renderBar(d))}
          </div>

          <div
            className="h-[8vw] w-[4%] bg-simulatorBg relative"
            style={{ zIndex: 3 }}
            aria-label="HT"
          />

          <div
            className="grid h-full w-[48%]"
            style={{
              gridTemplateColumns: "repeat(45, minmax(0, 1fr))",
              columnGap: "0.25vw",
            }}
          >
            {heatmapData.slice(MID_SLOTS, SLOT_COUNT).map((d) => renderBar(d))}
          </div>
        </div>
      </div>
    </div>
  );
};
